import ApiService from "@/core/services/api.service";
import {
    COORDINATOR_USER_ID,
    COORDINATOR_NAME,
} from "@/core/config/recruitmentOrders/recruitmentOrdersOption";

/**
 * Store module cho trang "Order tuyển nhân sự" (recruitment-orders).
 *
 * Viết theo đúng format của `user/users.module.js`: hằng số action mang giá trị
 * là endpoint, thân action chỉ là `new Promise` bọc `ApiService`, resolve `data`
 * hoặc reject `response` để component tự hiện message. Không còn fake data.
 *
 * Endpoint backend ĐÃ CÓ:
 *   GET    job-recruitment-requests        ?page,limit
 *   GET    job-recruitment-requests/{id}
 *   POST   job-recruitment-requests        {title, description, type}
 *   PUT    job-recruitment-requests/{id}   {title, description, type, status}
 *   DELETE job-recruitment-requests/{id}
 *
 *   type: 1 = Tuyển chính thức, 2 = Tuyển hộ (Giáo viên)
 *
 * Endpoint backend CHƯA CÓ (gọi sẽ lỗi cho tới khi BE bổ sung) — giữ đúng
 * contract mà FE đang chờ:
 *   GET    job-recruitment-requests/hr-team
 *   POST   job-recruitment-requests/{id}/approve
 *   POST   job-recruitment-requests/{id}/reject
 *   POST   job-recruitment-requests/{id}/remind-approval
 *   POST   job-recruitment-requests/{id}/assign
 *   POST   job-recruitment-requests/{id}/candidates   (multipart khi có file CV)
 *   POST   job-recruitment-requests/{id}/decide
 *   POST   job-recruitment-requests/{id}/cancel
 */

//action types
export const RO_GET_ORDERS = "job-recruitment/requests";
export const RO_GET_ORDER_DETAIL = "get-job-recruitment-request";
export const RO_CREATE_ORDER = "create-job-recruitment-request";
export const RO_UPDATE_ORDER = "update-job-recruitment-request";
export const RO_DELETE_ORDER = "delete-job-recruitment-request";
export const RO_GET_HR_TEAMLEADS = "job-recruitment/hr-team";
export const RO_APPROVE_ORDER = "job-recruitment/approve";
export const RO_REJECT_ORDER = "job-recruitment/reject";
export const RO_REMIND_APPROVAL = "job-recruitment/remind-approval";
export const RO_ASSIGN_ORDER = "job-recruitment/assign";
export const RO_SUBMIT_CANDIDATE = "job-recruitment/candidates";
export const RO_DECIDE_CANDIDATE = "job-recruitment/decide";
export const RO_CANCEL_ORDER = "job-recruitment/cancel";
export const RO_SEARCH_USERS = "roSearchUsers";
export const RO_GET_JOB_CATALOG = "job-recruitment/job-catalog";
export const RO_GET_BRANCHES = "job-recruitment/branches";
export const RO_ASSIGN_CV_APPROVER = "job-recruitment/assign-cv-approver";
export const RO_GET_TEACHER_GROUPS = "job-recruitment/teacher-groups";
export const RO_UPDATE_HIRING_START_DATE = "job-recruitment/hiring-start-date";
export const RO_GET_COORDINATED_JOBS = "job-recruitment/coordinated-jobs";
export const RO_GET_WORK_FORMS = "job-recruitment/work-forms";

//mutations types
export const RO_SET_ORDERS = "roSetOrders";
export const RO_SET_PAGINATION = "roSetPagination";
export const RO_SET_HR_TEAMLEADS = "roSetHrTeamLeads";
export const RO_SET_LOADING = "roSetLoading";
export const RO_UPSERT_ORDER = "roUpsertOrder";
export const RO_REMOVE_ORDER = "roRemoveOrder";
export const RO_SET_WORK_FORMS = "roSetWorkForms";

const state = {
    orders: [],
    pagination: {
        total: 0,
        currentPage: 1,
        perPage: 15,
        lastPage: 1,
    },
    hrTeamLeads: [],
    loading: false,
    workForms: [],
};

const getters = {
    roOrders(state) {
        return state.orders;
    },
    roPagination(state) {
        return state.pagination;
    },
    roHrTeamLeads(state) {
        return state.hrTeamLeads;
    },
    roLoading(state) {
        return state.loading;
    },
    roWorkForms(state) {
        return state.workForms;
    },
};

/** Body chung cho tạo mới / chỉnh sửa order */
function orderBody(payload) {
    const body = {};
    if (payload.title !== undefined || payload.position_title !== undefined) {
        body.title = payload.title || payload.position_title;
    }
    if (payload.description !== undefined) {
        body.description = payload.description;
    }
    if (payload.type !== undefined) {
        body.type = Number(payload.type);
    }
    if (payload.teamlead_approve !== undefined) {
        body.teamlead_approve = Number(payload.teamlead_approve);
    }
    if (payload.hr_approve !== undefined) {
        body.hr_approve = Number(payload.hr_approve);
    }
    if (payload.status !== undefined) {
        body.status = payload.status;
    }
    if (payload.person_in_charge_id !== undefined) {
        body.person_in_charge_id = payload.person_in_charge_id;
    }
    if (payload.cv_approver_id !== undefined) {
        body.cv_approver_id = payload.cv_approver_id !== null && payload.cv_approver_id !== "" ? Number(payload.cv_approver_id) : null;
    }
    if (payload.coordinator_id !== undefined) {
        body.coordinator_id = payload.coordinator_id !== null && payload.coordinator_id !== "" ? Number(payload.coordinator_id) : null;
    }
    if (payload.hr_reason_reject !== undefined) {
        body.hr_reason_reject = payload.hr_reason_reject;
    }
    if (payload.team_lead_reason_reject !== undefined) {
        body.team_lead_reason_reject = payload.team_lead_reason_reject;
    }
    if (payload.reject_reason !== undefined) {
        body.reject_reason = payload.reject_reason;
    }

    // Các trường chuẩn: category_id, detail_id, detail_name, branch_id, work_type, start_date, end_date, quantity
    if (payload.recruitment_job_category_id !== undefined || payload.job_catalog_id !== undefined) {
        body.recruitment_job_category_id = payload.recruitment_job_category_id !== undefined
            ? payload.recruitment_job_category_id
            : payload.job_catalog_id;
        body.job_catalog_id = body.recruitment_job_category_id;
    }
    if (payload.recruitment_job_detail_id !== undefined || payload.job_detail_id !== undefined) {
        body.recruitment_job_detail_id = payload.recruitment_job_detail_id !== undefined
            ? payload.recruitment_job_detail_id
            : payload.job_detail_id;
        body.job_detail_id = body.recruitment_job_detail_id;
    }
    if (payload.recruitment_job_detail_name !== undefined || payload.job_detail !== undefined) {
        body.recruitment_job_detail_name = payload.recruitment_job_detail_name !== undefined
            ? payload.recruitment_job_detail_name
            : payload.job_detail;
        body.job_detail = body.recruitment_job_detail_name;
    }
    if (payload.branch_id !== undefined) {
        body.branch_id = payload.branch_id;
    }
    if (payload.work_type !== undefined || payload.working_form !== undefined) {
        const wt = payload.work_type !== undefined ? payload.work_type : payload.working_form;
        body.work_type = wt !== null && wt !== "" && wt !== undefined ? Number(wt) : null;
        body.working_form = body.work_type;
    }
    if (payload.work_location !== undefined || payload.location !== undefined) {
        body.work_location = payload.work_location !== undefined ? payload.work_location : payload.location;
        body.location = body.work_location;
    }
    if (payload.start_date !== undefined || payload.start_time !== undefined) {
        body.start_date = payload.start_date || payload.start_time;
        body.start_time = body.start_date;
    }
    if (payload.end_date !== undefined || payload.end_time !== undefined) {
        body.end_date = payload.end_date || payload.end_time;
        body.end_time = body.end_date;
    }
    if (payload.quantity !== undefined || payload.amount !== undefined) {
        body.quantity = payload.quantity !== undefined ? Number(payload.quantity) : Number(payload.amount);
        body.amount = body.quantity;
    }
    if (payload.hiring_start_date !== undefined) {
        body.hiring_start_date = payload.hiring_start_date;
    }
    if (payload.code_order !== undefined) {
        body.code_order = payload.code_order !== null && payload.code_order !== "" ? String(payload.code_order).trim() : null;
    }

    // Nếu tạo mới mà chưa có type/title
    if (payload.id === undefined) {
        if (body.title === undefined) body.title = "";
        if (body.type === undefined) body.type = 1;
    }
    return body;
}

/** Gom mọi dạng meta phân trang BE có thể trả về một shape duy nhất */
function orderPaging(data, rows) {
    const meta = (data && data.pagination) || (data && data.meta && data.meta.pagination) || data || {};
    return {
        total: meta.total || rows.length,
        currentPage: meta.current_page || 1,
        perPage: meta.per_page || 15,
        lastPage: meta.last_page || 1,
    };
}

/** Payload có file CV -> gửi multipart */
function buildFormData(payload) {
    const form = new FormData();
    Object.keys(payload || {}).forEach((key) => {
        const value = payload[key];
        if (value === undefined || value === null) return;
        form.append(key, value);
    });
    return form;
}

const actions = {
    [RO_GET_ORDERS](context, payload) {
        context.commit(RO_SET_LOADING, true);
        return new Promise((resolve, reject) => {
            ApiService.query("job-recruitment/requests", payload).then(({ data }) => {
                const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                context.commit(RO_SET_ORDERS, list);
                context.commit(RO_SET_PAGINATION, orderPaging(data, list));
                context.commit(RO_SET_LOADING, false);
                resolve(data);
            }).catch((err) => {
                // Fallback nếu BE dùng path cũ
                ApiService.query("job-recruitment-requests", payload).then(({ data }) => {
                    const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                    context.commit(RO_SET_ORDERS, list);
                    context.commit(RO_SET_PAGINATION, orderPaging(data, list));
                    context.commit(RO_SET_LOADING, false);
                    resolve(data);
                }).catch(({ response }) => {
                    context.commit(RO_SET_ORDERS, []);
                    context.commit(RO_SET_LOADING, false);
                    reject(response || err);
                });
            });
        });
    },

    [RO_GET_ORDER_DETAIL](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        return new Promise((resolve, reject) => {
            ApiService.get("job-recruitment/requests", id).then(({ data }) => {
                resolve(data);
            }).catch(() => {
                ApiService.get("job-recruitment-requests", id).then(({ data }) => {
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
            });
        });
    },

    [RO_CREATE_ORDER](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post("job-recruitment/requests", orderBody(payload)).then(({ data }) => {
                if (data && (data.error === true || data.status === false || data.error_code)) {
                    reject(data);
                    return;
                }
                context.commit(RO_UPSERT_ORDER, data.data);
                resolve(data);
            }).catch((err) => {
                const res = err && (err.response || err);
                const resData = res && (res.data || res);
                if (resData && (resData.message_validate_form || (resData.data && resData.data.message_validate_form) || resData.error || (res && res.status === 422))) {
                    reject(res);
                    return;
                }
                ApiService.post("job-recruitment-requests", orderBody(payload)).then(({ data }) => {
                    if (data && (data.error === true || data.status === false || data.error_code)) {
                        reject(data);
                        return;
                    }
                    context.commit(RO_UPSERT_ORDER, data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response || err);
                });
            });
        });
    },

    [RO_UPDATE_ORDER](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.update("job-recruitment/requests", payload.id, orderBody(payload)).then(({ data }) => {
                if (data && (data.error === true || data.status === false || data.error_code)) {
                    reject(data);
                    return;
                }
                context.commit(RO_UPSERT_ORDER, data.data);
                resolve(data);
            }).catch((err) => {
                const res = err && (err.response || err);
                const resData = res && (res.data || res);
                if (resData && (resData.message_validate_form || (resData.data && resData.data.message_validate_form) || resData.error || (res && res.status === 422))) {
                    reject(res);
                    return;
                }
                ApiService.update("job-recruitment-requests", payload.id, orderBody(payload)).then(({ data }) => {
                    if (data && (data.error === true || data.status === false || data.error_code)) {
                        reject(data);
                        return;
                    }
                    context.commit(RO_UPSERT_ORDER, data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response || err);
                });
            });
        });
    },

    [RO_DELETE_ORDER](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        return new Promise((resolve, reject) => {
            // ApiService.delete bọc lỗi thành Error thường nên không còn `response`
            ApiService.delete("job-recruitment/requests", id).then(({ data }) => {
                context.commit(RO_REMOVE_ORDER, id);
                resolve(data);
            }).catch(() => {
                ApiService.delete("job-recruitment-requests", id).then(({ data }) => {
                    context.commit(RO_REMOVE_ORDER, id);
                    resolve(data);
                }).catch((error) => {
                    reject(error);
                });
            });
        });
    },

    [RO_GET_HR_TEAMLEADS](context, payload) {
        return new Promise((resolve, reject) => {
            const parseList = (res) => {
                if (!res) return [];
                if (Array.isArray(res)) return res;
                if (Array.isArray(res.data)) return res.data;
                if (res.data && Array.isArray(res.data.data)) return res.data.data;
                return [];
            };

            ApiService.query("job-recruitment/hr-team", payload)
                .then(({ data }) => {
                    const list = parseList(data);
                    context.commit(RO_SET_HR_TEAMLEADS, list);
                    resolve(list);
                })
                .catch(() => {
                    ApiService.query("job-recruitment-requests/hr-team", payload)
                        .then(({ data }) => {
                            const list = parseList(data);
                            context.commit(RO_SET_HR_TEAMLEADS, list);
                            resolve(list);
                        })
                        .catch(({ response }) => {
                            reject(response);
                        });
                });
        });
    },


    /**
     * Duyệt 1 cấp của order. Backend tự tính lại `status`:
     * đủ cấp duyệt -> chờ điều phối (order GV) hoặc đang tìm CV (order phòng ban).
     */
    [RO_APPROVE_ORDER](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`job-recruitment/requests/${payload.id}/approve`, payload).then(({ data }) => {
                context.commit(RO_UPSERT_ORDER, data.data);
                resolve(data);
            }).catch(() => {
                ApiService.post(`job-recruitment-requests/${payload.id}/approve`, payload).then(({ data }) => {
                    context.commit(RO_UPSERT_ORDER, data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
            });
        });
    },

    /** Từ chối 1 cấp -> cả order bị từ chối (order GV: chỉ cần 1 trong 2 cấp từ chối) */
    [RO_REJECT_ORDER](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`job-recruitment/requests/${payload.id}/reject`, payload).then(({ data }) => {
                context.commit(RO_UPSERT_ORDER, data.data);
                resolve(data);
            }).catch(() => {
                ApiService.post(`job-recruitment-requests/${payload.id}/reject`, payload).then(({ data }) => {
                    context.commit(RO_UPSERT_ORDER, data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
            });
        });
    },

    /** Gửi lại notification duyệt order tới các cấp còn đang chờ */
    [RO_REMIND_APPROVAL](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        return new Promise((resolve, reject) => {
            ApiService.post(`job-recruitment/requests/${id}/remind-approval`, {}).then(({ data }) => {
                context.commit(RO_UPSERT_ORDER, data.data);
                resolve(data);
            }).catch(() => {
                ApiService.post(`job-recruitment-requests/${id}/remind-approval`, {}).then(({ data }) => {
                    context.commit(RO_UPSERT_ORDER, data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
            });
        });
    },

    /** Điều phối viên giao order cho Teamlead HR phụ trách */
    [RO_ASSIGN_ORDER](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`job-recruitment/requests/${payload.id}/assign`, payload).then(({ data }) => {
                context.commit(RO_UPSERT_ORDER, data.data);
                resolve(data);
            }).catch(() => {
                ApiService.post(`job-recruitment-requests/${payload.id}/assign`, payload).then(({ data }) => {
                    context.commit(RO_UPSERT_ORDER, data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
            });
        });
    },

    /** Gửi CV ứng viên lên order; có file thì đẩy multipart */
    [RO_SUBMIT_CANDIDATE](context, payload) {
        const { id, file, ...rest } = payload;
        return new Promise((resolve, reject) => {
            const call = file
                ? ApiService.upload(`job-recruitment/requests/${id}/candidates`, buildFormData({ ...rest, cv_file: file }))
                : ApiService.post(`job-recruitment/requests/${id}/candidates`, rest);
            call.then(({ data }) => {
                context.commit(RO_UPSERT_ORDER, data.data && data.data.order ? data.data.order : data.data);
                resolve(data);
            }).catch(() => {
                const fallbackCall = file
                    ? ApiService.upload(`job-recruitment-requests/${id}/candidates`, buildFormData({ ...rest, cv_file: file }))
                    : ApiService.post(`job-recruitment-requests/${id}/candidates`, rest);
                fallbackCall.then(({ data }) => {
                    context.commit(RO_UPSERT_ORDER, data.data && data.data.order ? data.data.order : data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
            });
        });
    },

    /** Người đặt order chốt nhận / loại ứng viên đã được gửi lên */
    [RO_DECIDE_CANDIDATE](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`job-recruitment/requests/${payload.id}/decide`, payload).then(({ data }) => {
                context.commit(RO_UPSERT_ORDER, data.data);
                resolve(data);
            }).catch(() => {
                ApiService.post(`job-recruitment-requests/${payload.id}/decide`, payload).then(({ data }) => {
                    context.commit(RO_UPSERT_ORDER, data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
            });
        });
    },

    [RO_CANCEL_ORDER](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`job-recruitment/requests/${payload.id}/cancel`, payload).then(({ data }) => {
                context.commit(RO_UPSERT_ORDER, data.data);
                resolve(data);
            }).catch(() => {
                ApiService.post(`job-recruitment-requests/${payload.id}/cancel`, payload).then(({ data }) => {
                    context.commit(RO_UPSERT_ORDER, data.data);
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
            });
        });
    },

    /** Tìm kiếm nhân sự để gán người phụ trách (gọi user/filter hoặc fallback list-user) */
    [RO_SEARCH_USERS](context, payload) {
        const keyword = typeof payload === "string" ? payload : (payload && payload.keyword) || "";
        return new Promise((resolve) => {
            ApiService.query("user/filter", { keyword })
                .then(({ data }) => {
                    const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                    resolve(list);
                })
                .catch(() => {
                    ApiService.query("/list-user", { keyword, limit: 20 })
                        .then(({ data }) => {
                            const list = (data && data.data && (data.data.data || data.data)) || [];
                            resolve(Array.isArray(list) ? list : []);
                        })
                        .catch(() => resolve([]));
                });
        });
    },

    /** Lấy danh mục vị trí / mẫu tuyển dụng từ /job-recruitment/job-catalog */
    [RO_GET_JOB_CATALOG]() {
        return new Promise((resolve, reject) => {
            ApiService.query("job-recruitment/job-catalog")
                .then(({ data }) => {
                    const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                    resolve(list);
                })
                .catch(() => {
                    ApiService.query("job-recruitment/job-catalog")
                        .then(({ data }) => {
                            const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                            resolve(list);
                        })
                        .catch(() => {
                            // Fallback tới path cũ
                            ApiService.get("job-recruitment-job-catalog")
                                .then(({ data }) => {
                                    const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                                    resolve(list);
                                })
                                .catch((e) => reject(e));
                        });
                });
        });
    },

    /** Lấy danh sách địa điểm / chi nhánh từ /job-recruitment/branches */
    [RO_GET_BRANCHES]() {
        return new Promise((resolve, reject) => {
            ApiService.query("job-recruitment/branches")
                .then(({ data }) => {
                    const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                    resolve(list);
                })
                .catch(() => {
                    ApiService.query("job-recruitment/branches")
                        .then(({ data }) => {
                            const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                            resolve(list);
                        })
                        .catch(() => {
                            // Fallback gọi path cũ job-recruitment-branches hoặc /branch
                            ApiService.get("job-recruitment-branches")
                                .then(({ data }) => {
                                    const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                                    resolve(list);
                                })
                                .catch(() => {
                                    ApiService.query("/branch")
                                        .then(({ data }) => {
                                            const list = (data && data.data && (data.data.data || data.data)) || (Array.isArray(data) ? data : []);
                                            resolve(Array.isArray(list) ? list : []);
                                        })
                                        .catch((e) => reject(e));
                                });
                        });
                });
        });
    },

    /** Lấy danh sách hình thức làm việc từ /job-recruitment/work-forms */
    [RO_GET_WORK_FORMS](context, payload = {}) {
        return new Promise((resolve, reject) => {
            ApiService.query("job-recruitment/work-forms", payload)
                .then(({ data }) => {
                    const resData = data && data.data !== undefined ? data.data : data;
                    let list = [];
                    if (Array.isArray(resData)) {
                        list = resData;
                    } else if (resData && Array.isArray(resData.data)) {
                        list = resData.data;
                    } else if (resData && Array.isArray(resData.list)) {
                        list = resData.list;
                    } else if (resData && Array.isArray(resData.items)) {
                        list = resData.items;
                    }
                    context.commit(RO_SET_WORK_FORMS, list);
                    resolve(list);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    /** Gán người duyệt CV cho yêu cầu tuyển dụng */
    [RO_ASSIGN_CV_APPROVER](context, payload) {
        return new Promise((resolve, reject) => {
            const body = {
                cv_approver_id: payload.cv_approver_id !== undefined && payload.cv_approver_id !== null && payload.cv_approver_id !== ""
                    ? Number(payload.cv_approver_id)
                    : null,
            };

            const applyMergedOrder = (resData) => {
                const updated = (resData && resData.data) ? resData.data : (resData || {});
                const merged = {
                    ...updated,
                    id: payload.id,
                    cv_approver_id: body.cv_approver_id,
                };
                if (payload.cv_approver) {
                    merged.cv_approver = payload.cv_approver;
                    merged.cv_approver_name = payload.cv_approver.name || "";
                } else if (body.cv_approver_id == null) {
                    merged.cv_approver = null;
                    merged.cv_approver_name = "";
                } else if (String(body.cv_approver_id) === String(COORDINATOR_USER_ID)) {
                    merged.cv_approver = { id: COORDINATOR_USER_ID, name: COORDINATOR_NAME };
                    merged.cv_approver_name = COORDINATOR_NAME;
                }
                context.commit(RO_UPSERT_ORDER, merged);
            };

            // Ưu tiên endpoint POST requests/:id/assign-cv-approver, fallback PUT requests/:id
            ApiService.post(`job-recruitment/requests/${payload.id}/assign-cv-approver`, body)
                .then(({ data }) => {
                    applyMergedOrder(data);
                    resolve(data);
                })
                .catch(() => {
                    ApiService.post("job-recruitment/assign-cv-approver", { request_id: payload.id, id: payload.id, ...body })
                        .then(({ data }) => {
                            applyMergedOrder(data);
                            resolve(data);
                        })
                        .catch(() => {
                            ApiService.update("job-recruitment/requests", payload.id, body)
                                .then(({ data }) => {
                                    applyMergedOrder(data);
                                    resolve(data);
                                })
                                .catch(() => {
                                    // Fallback tới path cũ nếu backend chưa migrate endpoint
                                    ApiService.update("job-recruitment-requests", payload.id, body)
                                        .then(({ data }) => {
                                            applyMergedOrder(data);
                                            resolve(data);
                                        })
                                        .catch(({ response }) => reject(response));
                                });
                        });
                });
        });
    },

    /** Lấy danh sách nhân sự từ API job-recruitment/teacher-groups */
    [RO_GET_TEACHER_GROUPS](context, payload) {
        const params = typeof payload === "string" ? { keyword: payload } : (payload || {});
        return new Promise((resolve) => {
            const processData = (rawData) => {
                let list = [];
                if (!rawData) list = [];
                else if (Array.isArray(rawData)) list = rawData;
                else if (Array.isArray(rawData.data)) list = rawData.data;
                else if (rawData.data && Array.isArray(rawData.data.data)) list = rawData.data.data;
                else if (rawData.data && typeof rawData.data === "object") list = Object.values(rawData.data);

                const map = new Map();
                const addOne = (item, groupName = "") => {
                    if (!item || typeof item !== "object") return;
                    const userObj = (item.user && typeof item.user === "object")
                        ? item.user
                        : (item.leader && typeof item.leader === "object"
                            ? item.leader
                            : (item.lead && typeof item.lead === "object" ? item.lead : item));

                    // Ưu tiên id của user account (vì cv_approver_id liên kết bảng users)
                    let id = (item.user && item.user.id) ||
                        (item.leader && item.leader.id) ||
                        (item.lead && item.lead.id) ||
                        item.user_id ||
                        item.leader_id ||
                        item.lead_id ||
                        item.account_id ||
                        item.teacher_id ||
                        item.id;

                    let name = (userObj && (userObj.name || userObj.full_name || userObj.username || userObj.user_name)) ||
                        item.name || item.full_name || item.username || item.user_name || item.teacher_name || "";

                    // Đảm bảo "Ms. Cẩm Tú" / "Chị Cẩm Tú" nhận đúng COORDINATOR_USER_ID = 332991 (tránh bị pivot/group id = 1 đè thành Test_account)
                    const lowerName = (name || "").toLowerCase();
                    if (lowerName.includes("cẩm tú") || lowerName.includes("cam tu")) {
                        if (!id || id === 1 || String(id) === "1") {
                            id = COORDINATOR_USER_ID;
                        }
                        if (!name || name === "User #1") {
                            name = "Ms. Cẩm Tú";
                        }
                    }

                    if (!id) return;
                    const idKey = String(id);
                    if (!name) name = `User #${id}`;
                    const email = userObj.email || item.email || "";
                    const phone = userObj.phone || userObj.mobile || item.phone || item.mobile || "";
                    const dept = userObj.department_name || (userObj.department && userObj.department.name) ||
                        item.department_name || (item.department && item.department.name) ||
                        groupName || item.group_name || "";

                    if (!map.has(idKey)) {
                        map.set(idKey, {
                            id: Number(id),
                            name,
                            email,
                            phone,
                            department_name: dept,
                            ...item,
                            ...(typeof userObj === "object" ? userObj : {}),
                            id: Number(id),
                            name,
                        });
                    }
                };

                list.forEach((item) => {
                    if (!item || typeof item !== "object") return;
                    const gName = item.name || item.group_name || item.title || "";
                    if (item.leader && typeof item.leader === "object") {
                        addOne(item.leader, gName);
                    }
                    if (item.team_lead && typeof item.team_lead === "object") {
                        addOne(item.team_lead, gName);
                    }
                    if (item.lead && typeof item.lead === "object") {
                        addOne(item.lead, gName);
                    }
                    if (item.manager && typeof item.manager === "object") {
                        addOne(item.manager, gName);
                    }
                    if (Array.isArray(item.teachers) && item.teachers.length > 0) {
                        item.teachers.forEach((t) => addOne(t, gName));
                    } else if (Array.isArray(item.users) && item.users.length > 0) {
                        item.users.forEach((u) => addOne(u, gName));
                    } else if (Array.isArray(item.members) && item.members.length > 0) {
                        item.members.forEach((m) => addOne(m, gName));
                    } else if (Array.isArray(item.children) && item.children.length > 0) {
                        item.children.forEach((c) => addOne(c, gName));
                    } else if (item.user && typeof item.user === "object") {
                        addOne(item.user, gName);
                    } else {
                        addOne(item, "");
                    }
                });

                // Luôn đảm bảo Ms. Cẩm Tú có mặt trong danh sách lựa chọn người duyệt CV
                if (!map.has(String(COORDINATOR_USER_ID))) {
                    map.set(String(COORDINATOR_USER_ID), {
                        id: COORDINATOR_USER_ID,
                        name: "Ms. Cẩm Tú",
                        department_name: "Phòng Tổ chức Giáo viên",
                        email: "",
                        phone: "",
                    });
                }

                let results = Array.from(map.values());
                if (params.keyword && typeof params.keyword === "string" && params.keyword.trim()) {
                    const kw = params.keyword.toLowerCase().trim();
                    results = results.filter((u) =>
                        (u.name && u.name.toLowerCase().includes(kw)) ||
                        (u.email && u.email.toLowerCase().includes(kw)) ||
                        (u.phone && String(u.phone).includes(kw)) ||
                        (u.department_name && u.department_name.toLowerCase().includes(kw))
                    );
                }
                return results;
            };

            ApiService.query("job-recruitment/teacher-groups", params)
                .then(({ data }) => {
                    const results = processData(data);
                    resolve(results);
                })
                .catch(() => {
                    ApiService.get("job-recruitment/teacher-groups")
                        .then(({ data }) => {
                            const results = processData(data);
                            resolve(results);
                        })
                        .catch(() => {
                            // Fallback RO_SEARCH_USERS nếu endpoint teacher-groups chưa sẵn sàng
                            context.dispatch(RO_SEARCH_USERS, params)
                                .then((fallbackList) => resolve(Array.isArray(fallbackList) ? fallbackList : []))
                                .catch(() => resolve([]));
                        });
                });
        });
    },

    /**
     * Cập nhật ngày bắt đầu chạy job (hiring_start_date) do HR phụ trách nhập
     */
    [RO_UPDATE_HIRING_START_DATE](context, payload) {
        return new Promise((resolve, reject) => {
            const body = {
                hiring_start_date: payload.hiring_start_date,
            };
            const applyMerged = (resData) => {
                const updated = (resData && resData.data) ? resData.data : (resData || {});
                context.commit(RO_UPSERT_ORDER, {
                    ...payload,
                    ...updated,
                    id: payload.id,
                    hiring_start_date: payload.hiring_start_date,
                });
            };

            // Ưu tiên PUT job-recruitment/requests/:id
            ApiService.update("job-recruitment/requests", payload.id, body)
                .then(({ data }) => {
                    applyMerged(data);
                    resolve(data);
                })
                .catch((err) => {
                    // Fallback thử endpoint POST /hiring-start-date nếu BE thiết kế dạng action
                    ApiService.post(`job-recruitment/requests/${payload.id}/hiring-start-date`, body)
                        .then(({ data }) => {
                            applyMerged(data);
                            resolve(data);
                        })
                        .catch(() => {
                            // Fallback PUT job-recruitment-requests
                            ApiService.update("job-recruitment-requests", payload.id, body)
                                .then(({ data }) => {
                                    applyMerged(data);
                                    resolve(data);
                                })
                                .catch(({ response }) => {
                                    reject(response || err);
                                });
                        });
                });
        });
    },

    /**
     * Lấy danh sách job được gán cho người điều phối đang đăng nhập
     * GET job-recruitment/coordinated-jobs
     */
    [RO_GET_COORDINATED_JOBS](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.query("job-recruitment/coordinated-jobs", payload)
                .then(({ data }) => {
                    const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
                    resolve({ data, list, pagination: orderPaging(data, list) });
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },
};

const mutations = {
    [RO_SET_ORDERS](state, data) {
        state.orders = Array.isArray(data) ? data : [];
    },
    [RO_SET_PAGINATION](state, data) {
        if (!data) return;
        state.pagination = {
            total: data.total || 0,
            currentPage: data.currentPage || data.current_page || 1,
            perPage: data.perPage || data.per_page || 15,
            lastPage: data.lastPage || data.last_page || 1,
        };
    },
    [RO_SET_HR_TEAMLEADS](state, data) {
        state.hrTeamLeads = Array.isArray(data) ? data : [];
    },
    [RO_SET_LOADING](state, value) {
        state.loading = !!value;
    },
    [RO_UPSERT_ORDER](state, order) {
        if (!order) return;
        const idx = state.orders.findIndex((o) => String(o.id) === String(order.id));
        if (idx === -1) {
            state.orders.unshift(order);
        } else {
            state.orders.splice(idx, 1, { ...state.orders[idx], ...order });
        }
    },
    [RO_REMOVE_ORDER](state, id) {
        state.orders = state.orders.filter((o) => String(o.id) !== String(id));
    },
    [RO_SET_WORK_FORMS](state, data) {
        state.workForms = Array.isArray(data) ? data : [];
    },
};

export default {
    state,
    actions,
    mutations,
    getters,
};
